<template>
  <div class="game-slider-page">
    <SiteHeader :config="configData" :query-string="queryString" />

    <!-- Larger Banner for Mobile when enabled -->
    <LargerBanner
      v-if="isEnableLargeBanner && getDeviceType() === 'mobile'"
      :slides="mobileBannerSlides"
      :image-url="imageUrl"
      :query-string="queryString"
    />
    <!-- Default Banner when LargerBanner is not enabled -->
    <Banner
      v-else-if="!isEnableLargeBanner"
      :slides="slides"
      :image-url="imageUrl"
      :query-string="queryString"
    />
    <div class="page-header">
      <h1>{{ $t('bestSellingGames') }}</h1>
    </div>
    <game-list-slider
      :games="games"
      :image-url="imageUrl"
      :query-string="queryString"
    />
    <footer class="footer">
      <div class="footer-content">
        <a :href="`${h365Url}${langMap}${queryString ? '?' + queryString : ''}`">
          <div class="footer-logo">
            <img :src="`${imageUrl}/${logoImage}`" />
          </div>
        </a>
        <div class="footer-txt" v-html="$t('footerMessage')"></div>
      </div>
      <div class="copyright">copyright ©H365 All rights reserved.</div>
    </footer>
  </div>
</template>

<script>
import { configurationLocale } from '@/utils/navigation'
import SiteHeader from '@/components/SiteHeader.vue'
import GameListSlider from '@/components/GameListSlider.vue'
import LargerBanner from '@/components/LargerBanner.vue'
import Banner from '@/components/Banner.vue'
import { getCloudStorage } from '@/plugin/axios'
import qs from 'qs'

export default {
  name: 'GameSliderPage',
  components: {
    SiteHeader,
    GameListSlider,
    LargerBanner,
    Banner
  },
  props: {
    isEnableLargeBanner: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      queryString: '',
      configData: {},
      games: [],
      slides: [],
      mobileBannerSlides: []
    }
  },
  async created() {
    this.queryString = window.location.search.slice(1)
    const query = qs.parse(this.queryString)
    const filename = query.utm_source ? query.utm_source : 'default'
    const url = encodeURIComponent(`config/minisite/${filename}.json`)
    try {
      const timestamp = Math.floor(Date.now() / (5 * 600))
      console.log('🌐 GameSliderPage Request:', {
        url: url,
        fullUrl: `${process.env.VUE_APP_CLOUD_STORAGE_URL}/storage/v1/b/h365-landing-page/o/${url}`,
        params: { alt: 'media', t: timestamp },
        method: 'GET'
      })

      const { data } = await getCloudStorage.get(url, { params: { alt: 'media', t: timestamp } })
      if (data) {
        this.configData = data
        if (this.$i18n.messages[configurationLocale(data.lang)]) this.$i18n.locale = configurationLocale(data.lang)
        if (data?.games) {
          const width = this.isDesktop() ? 256 : 384
          this.games = data.games.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        if (data?.slides) {
          const width = this.isDesktop() ? 1360 : 428
          this.slides = data.slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        if (data?.mobile_banner_slides) {
          const width = 428 // Mobile only, fixed width
          this.mobileBannerSlides = data.mobile_banner_slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
      }
    }
    catch (error) {
      console.error('❌ GameSliderPage Error:', error)
      const defaultUrl = encodeURIComponent('config/minisite/default.json')

      const timestamp = Math.floor(Date.now() / (5 * 600))
      console.log('🔄 GameSliderPage Fallback Request:', {
        url: defaultUrl,
        fullUrl: `${process.env.VUE_APP_CLOUD_STORAGE_URL}/storage/v1/b/h365-landing-page/o/${defaultUrl}`,
        params: { alt: 'media', t: timestamp },
        method: 'GET'
      })

      const { data } = await getCloudStorage.get(defaultUrl, { params: { alt: 'media', t: timestamp } })
      if (data) {
        this.configData = data
        if (this.$i18n.messages[configurationLocale(data.lang)]) this.$i18n.locale = configurationLocale(data.lang)
        if (data?.games) {
          const width = this.isDesktop() ? 256 : 384
          this.games = data.games.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        if (data?.slides) {
          const width = this.isDesktop() ? 1360 : 428
          this.slides = data.slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        if (data?.mobile_banner_slides) {
          const width = 428 // Mobile only, fixed width
          this.mobileBannerSlides = data.mobile_banner_slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
      }
    }
  },
  computed: {
    imageUrl() {
      if (process.env.VUE_APP_IMAGE_URL)
        return process.env.VUE_APP_IMAGE_URL
      return ''
    },
    imagePath() {
      if (process.env.VUE_APP_IMAGE_PATH)
        return process.env.VUE_APP_IMAGE_PATH
      return ''
    },
    h365Url() {
      if (this.configData?.logoURL)
        return this.configData.logoURL
      return ''
    },
    langMap() {
      if (this.$i18n.locale === 'zh-CN')
        return ''
      return `/${this.$i18n.locale}`
    },
    logoImage() {
      const width = this.isDesktop() ? 140 : 119
      return `${width}/h365-landing-page/config/minisite/image/h365-logo-reverse.png`
    }
  },
  methods: {
    isDesktop() {
      return document.body.getBoundingClientRect().width > 1024
    },
    getDeviceType() {
      const width = document.body.getBoundingClientRect().width
      if (width <= 768) return 'mobile'
      if (width > 1024) return 'desktop'
      return 'tablet'
    }
  }
}
</script>

<style lang="scss" scoped>
.game-slider-page {
  min-height: 100vh;
  background-color: #1c1c1c;
  padding: 20px 0;

  .page-header {
    text-align: center;
    margin-top: 24px;
    margin-bottom: 12px;
    padding: 8px 0 0 0;

    h1 {
      color: #ffffff;
      font-size: 32px;
      font-weight: bold;
      margin: 0;
      position: relative;
      display: inline-block;

      &::after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 3px;
        background: #007bff;
        border-radius: 2px;
      }
    }
  }
}


@media (max-width: 768px) {
  .game-slider-page {
    padding: 15px 0;

    .page-header {
      margin-top: 18px;
      margin-bottom: 8px;
      padding: 6px 0 0 0;

      h1 {
        font-size: 28px;
      }
    }
  }


}

@media (max-width: 480px) {
  .game-slider-page {
    padding: 10px 0;

    .page-header {
      margin-top: 24px;
      margin-bottom: 4px;
      padding: 4px 0 0 0;

      h1 {
        font-size: 24px;
      }
    }
  }


}
</style>